import { useState, useEffect } from 'react';
import { apiJsonFetch } from '../utils/api';
import {
  Plus,
  Box,
  RefreshCw,
  CheckCircle,
  XCircle,
  Settings,
  Trash2,
  X,
  AlertCircle,
} from 'lucide-react';

interface WMSConnection {
  id: string;
  wmsType: string;
  wmsName: string;
  connectionType: 'api' | 'ftp';
  syncEnabled: boolean;
  lastSyncAt?: string;
}

const wmsTypes = [
  { value: 'logizard', label: 'ロジザード' },
  { value: 'openlogi', label: 'オープンロジ' },
  { value: 'w3mimosa', label: 'W3 mimosa' },
  { value: 'other', label: 'その他' },
];

export default function WMSPage() {
  const [wmsConnections, setWmsConnections] = useState<WMSConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);

  useEffect(() => {
    fetchWMSConnections();
  }, []);

  const fetchWMSConnections = async () => {
    try {
      setLoading(true);
      const res = await apiJsonFetch('/api/wms');
      setWmsConnections(res.data.wmsConnections || []);
    } catch (error) {
      console.error('WMS連携取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (wmsId: string, type: 'inventory' | 'shipments') => {
    try {
      setSyncing(wmsId);
      const res = await apiJsonFetch(`/api/wms/${wmsId}/sync/${type}`, { method: 'POST' });
      if (res.ok && res.data.success) {
        alert(`✓ ${type === 'inventory' ? '在庫' : '出荷'}同期\n${res.data.message}`);
      } else {
        alert(`✗ 同期失敗\n${res.data?.message || res.data?.error || '不明なエラー'}`);
      }
      fetchWMSConnections();
    } catch (error: any) {
      console.error('同期エラー:', error);
      alert(`同期に失敗しました: ${error.message || '不明なエラー'}`);
    } finally {
      setSyncing(null);
    }
  };

  const handleTestConnection = async (wmsId: string) => {
    try {
      const res = await apiJsonFetch(`/api/wms/${wmsId}/test-connection`, { method: 'POST' });
      if (res.ok && res.data.success) {
        const details = res.data.details;
        alert(`✓ 接続テスト成功\n${res.data.message}\n\nタイプ: ${details?.wmsType}\n接続: ${details?.connectionType}\nエンドポイント: ${details?.endpoint || 'N/A'}`);
      } else {
        alert(`✗ 接続テスト失敗\n${res.data?.message || '不明なエラー'}`);
      }
    } catch (error: any) {
      console.error('接続テストエラー:', error);
      alert(`接続テストに失敗しました: ${error.message || '不明なエラー'}`);
    }
  };

  const handleDelete = async (wmsId: string) => {
    if (!confirm('このWMS連携を削除しますか？')) return;
    try {
      await apiJsonFetch(`/api/wms/${wmsId}`, { method: 'DELETE' });
      fetchWMSConnections();
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WMS連携</h1>
          <p className="text-sm text-gray-500 mt-1">倉庫管理システムとの連携設定</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          WMS追加
        </button>
      </div>

      {/* WMS連携一覧 */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">読み込み中...</p>
        </div>
      ) : wmsConnections.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Box className="h-12 w-12 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-2">WMS連携が設定されていません</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
          >
            WMS連携を追加する
          </button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {wmsConnections.map((wms) => (
            <div
              key={wms.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              {/* ヘッダー */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Box className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{wms.wmsName}</h3>
                    <p className="text-sm text-gray-500">
                      {wmsTypes.find((t) => t.value === wms.wmsType)?.label || wms.wmsType}
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    wms.syncEnabled
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {wms.syncEnabled ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      有効
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3" />
                      無効
                    </>
                  )}
                </span>
              </div>

              {/* 情報 */}
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">接続方式</span>
                  <span className="text-gray-900 font-medium">
                    {wms.connectionType === 'api' ? 'API' : 'FTP'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">最終同期</span>
                  <span className="text-gray-900">
                    {wms.lastSyncAt
                      ? new Date(wms.lastSyncAt).toLocaleString('ja-JP')
                      : '未実行'}
                  </span>
                </div>
              </div>

              {/* アクション */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleSync(wms.id, 'inventory')}
                  disabled={syncing === wms.id}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${syncing === wms.id ? 'animate-spin' : ''}`}
                  />
                  在庫同期
                </button>
                <button
                  onClick={() => handleSync(wms.id, 'shipments')}
                  disabled={syncing === wms.id}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${syncing === wms.id ? 'animate-spin' : ''}`}
                  />
                  出荷連携
                </button>
                <button
                  onClick={() => handleTestConnection(wms.id)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  title="接続テスト"
                >
                  <Settings className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(wms.id)}
                  className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  title="削除"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* WMS追加モーダル */}
      {showAddModal && (
        <AddWMSModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchWMSConnections();
          }}
        />
      )}
    </div>
  );
}

// WMS追加モーダル
function AddWMSModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    wmsType: 'logizard',
    wmsName: '',
    connectionType: 'api' as 'api' | 'ftp',
    apiEndpoint: '',
    apiKey: '',
    ftpHost: '',
    ftpPort: 21,
    ftpUser: '',
    ftpPassword: '',
    syncEnabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await apiJsonFetch('/api/wms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">WMS連携追加</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WMSタイプ *</label>
              <select
                value={formData.wmsType}
                onChange={(e) => setFormData({ ...formData, wmsType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {wmsTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WMS名 *</label>
              <input
                type="text"
                value={formData.wmsName}
                onChange={(e) => setFormData({ ...formData, wmsName: e.target.value })}
                required
                placeholder="例: 東京倉庫"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">接続方式 *</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="connectionType"
                    value="api"
                    checked={formData.connectionType === 'api'}
                    onChange={() => setFormData({ ...formData, connectionType: 'api' })}
                  />
                  <span>API</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="connectionType"
                    value="ftp"
                    checked={formData.connectionType === 'ftp'}
                    onChange={() => setFormData({ ...formData, connectionType: 'ftp' })}
                  />
                  <span>FTP</span>
                </label>
              </div>
            </div>

            {formData.connectionType === 'api' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">APIエンドポイント</label>
                  <input
                    type="url"
                    value={formData.apiEndpoint}
                    onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
                    placeholder="https://api.example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">APIキー</label>
                  <input
                    type="password"
                    value={formData.apiKey}
                    onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FTPホスト</label>
                    <input
                      type="text"
                      value={formData.ftpHost}
                      onChange={(e) => setFormData({ ...formData, ftpHost: e.target.value })}
                      placeholder="ftp.example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ポート</label>
                    <input
                      type="number"
                      value={formData.ftpPort}
                      onChange={(e) => setFormData({ ...formData, ftpPort: parseInt(e.target.value) || 21 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FTPユーザー</label>
                    <input
                      type="text"
                      value={formData.ftpUser}
                      onChange={(e) => setFormData({ ...formData, ftpUser: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FTPパスワード</label>
                    <input
                      type="password"
                      value={formData.ftpPassword}
                      onChange={(e) => setFormData({ ...formData, ftpPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="syncEnabled"
                checked={formData.syncEnabled}
                onChange={(e) => setFormData({ ...formData, syncEnabled: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="syncEnabled" className="text-sm text-gray-700">同期を有効にする</label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                キャンセル
              </button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? '追加中...' : '追加'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

